import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Swords, Loader2, Trophy, Star, Eye, Radio, Coins, Radar, Gift, Timer, Wrench, X, RefreshCw } from 'lucide-react';

import { GbBattle } from './GbBattle';
import { GbClanBadge } from './GbClanBadge';
import { GbRoom, GbMatchFx } from './GbRoom';
import { gbSfx } from './gbSfx';
import { gbAviso } from './GbNotice';

const wsUrl = () => {
  const base = process.env.REACT_APP_BACKEND_URL || '';
  const proto = base.startsWith('https') ? 'wss' : 'ws';
  return `${base.replace(/^https?/, proto)}/api/gb/pvp/ws`;
};

const mmss = (s) => {
  const v = Math.max(0, Number(s || 0));
  return `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
};

// um lado do duelo ao vivo: avatar, nick, tag do clã e barra de vida
const Lutador = ({ p, max, dir }) => {
  const pct = Math.max(2, Math.round((Number(p.hp || 0) / Math.max(1, Number(max || 300))) * 100));
  return (
    <div className={`flex min-w-0 flex-1 flex-col gap-1.5 ${dir ? 'items-end' : ''}`}>
      <div className={`flex min-w-0 items-center gap-2 ${dir ? 'flex-row-reverse' : ''}`}>
        {p.avatar
          ? <img src={p.avatar} alt="" className="h-10 w-10 flex-none rounded-full border-2 object-cover"
            style={{ borderColor: p.hex || 'rgba(251,191,36,.65)' }} />
          : <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full border-2 border-amber-500/50 bg-purple-900 text-base">🎃</span>}
        <div className={`min-w-0 ${dir ? 'text-right' : ''}`}>
          <p className="truncate text-xs font-black text-amber-100">{p.nickname}</p>
          <div className={`mt-0.5 flex min-w-0 items-center gap-1 ${dir ? 'justify-end' : ''}`}>
            <GbClanBadge clan={{ name: p.clan_name || p.clan, medal_img: p.medal_img, hex: p.hex }} size="xs" />
            <span className="flex-none text-[9px] font-bold text-purple-300/60">Nv {p.level}</span>
          </div>
        </div>
      </div>
      <span className="block h-2 w-full overflow-hidden rounded-full border border-black/50 bg-purple-950">
        <span className={`block h-full rounded-full ${dir
          ? 'ml-auto bg-gradient-to-l from-rose-500 to-rose-300' : 'bg-gradient-to-r from-emerald-500 to-emerald-300'}`}
          style={{ width: `${pct}%` }} />
      </span>
      <span className="font-mono text-[9px] font-black text-purple-300/70">{p.hp} HP</span>
    </div>
  );
};

export const GbPvp = ({ player, clan, avatarUrl, config, travado, avisoTrava, onFinished, onWallet, onFase }) => {
  const [fase, setFase] = useState('idle');     // idle | sala | fila | match | partida | assistindo
  const [room, setRoom] = useState({ members: [], searching: 0, search_seconds: 120 });
  const [restante, setRestante] = useState(0);
  const [fx, setFx] = useState(null);            // efeito de match encontrado
  const [afk, setAfk] = useState(null);          // oponente caiu
  const [match, setMatch] = useState(null);
  const [lobby, setLobby] = useState({ online: 0, queue: 0, matches: [] });
  const [plateia, setPlateia] = useState(0);
  const [regras, setRegras] = useState(false);
  const [atualizando, setAtualizando] = useState(false);
  const [conectado, setConectado] = useState(false);
  const [convite, setConvite] = useState(null);    // convite direto que eu recebi
  const [enviados, setEnviados] = useState([]);    // convites que eu mandei e aguardam resposta
  const ws = useRef(null);
  const paraJogo = useRef(null);
  const flip = useRef(null);
  const faseRef = useRef('idle');
  const medidor = useRef(null);
  const medidoEm = useRef(0);
  const conectarRef = useRef(null);
  const espectando = useRef(false);
  const assistindoId = useRef('');                 // qual partida eu estou assistindo
  const fimAssistido = useRef(false);              // a partida que eu assistia já acabou
  const tentativas = useRef(0);                    // tentativas de reconexão seguidas
  const pingEnv = useRef(0);
  

  const fechar = useCallback(() => {
    const s = ws.current;
    ws.current = null;
    if (!s) return;
    s.manual = true;                 // fechamos de propósito: nada de aviso de erro
    try { s.close(); } catch (e) { /* noop */ }
  }, []);

  // pede a lista de partidas ao vivo (reconectando se o socket tiver caído)
  const pedirLobby = useCallback(() => {
    conectarRef.current?.(() => {
      try { ws.current?.send(JSON.stringify({ t: 'lobby' })); } catch (e) { /* noop */ }
    });
  }, []);

  // sai da transmissão e volta para a arena (partida acabou, ou o jogador pediu)
  const sairDaTransmissao = useCallback((texto) => {
    try { ws.current?.send(JSON.stringify({ t: 'unwatch' })); } catch (e) { /* noop */ }
    espectando.current = false;
    assistindoId.current = '';
    fimAssistido.current = false;
    setMatch(null); setPlateia(0); setFase('idle');
    if (texto) gbAviso.info(texto, 4200);
    pedirLobby();
  }, [pedirLobby]);

  // dentro da batalha o aviso aparece no jogo; fora dela, na página
  const aviso = useCallback((text) => {
    if (paraJogo.current) paraJogo.current('gb:note', { text, color: '#ff9db0' });
    else gbAviso.erro(text);
  }, []);

  const conectar = useCallback((depois) => {
    const atual = ws.current;
    if (atual && atual.readyState === 1) { depois?.(); return; }
    // ainda abrindo: pendura a tarefa na mesma conexão.
    // abrir uma segunda faria o servidor derrubar a sessão ("sessao_substituida")
    if (atual && atual.readyState === 0) {
      if (depois) atual.fila = [...(atual.fila || []), depois];
      return;
    }
    if (atual) fechar();
    const s = new WebSocket(`${wsUrl()}?token=${encodeURIComponent(localStorage.getItem('customer_token') || '')}`);
    s.fila = depois ? [depois] : [];
    ws.current = s;
    s.onopen = () => {
      s.aberto = true; s.ultimo = Date.now(); s.send(JSON.stringify({ t: 'lobby' }));
      tentativas.current = 0; setConectado(true);
      const fila = s.fila || []; s.fila = [];
      fila.forEach((fn) => { try { fn(); } catch (e) { /* noop */ } });
      s.pingId = setInterval(() => {
        if (s.readyState !== 1) return;
        // vigia da conexão: celular que troca de rede às vezes deixa o socket "vivo e mudo".
        // O servidor manda pong a cada 1s e pulso da partida a cada 2s: 4s de silêncio
        // total só acontece com a conexão morta. Derruba e reconecta na hora.
        if (Date.now() - (s.ultimo || 0) > 4000) { try { s.close(); } catch (e) { /* noop */ } return; }
        try { s.send(JSON.stringify({ t: 'ping', ts: Math.round(performance.now()) })); } catch (e) { /* noop */ }
      }, 1000);

    };
    s.onclose = () => {
      clearInterval(s.pingId);
      if (ws.current !== s) return;          // conexão velha: quem manda é a nova
      ws.current = null;
      setConectado(false);
      // o jogo foi aberto em outro aparelho/aba: essa tela fica quieta, sem reconectar
      if (s.substituida) return;
      const f = faseRef.current;
      const voltar = () => conectarRef.current?.(() => {
        const f2 = faseRef.current;
        if (f2 === 'sala' || f2 === 'fila') { try { ws.current?.send(JSON.stringify({ t: 'room_join' })); } catch (e) { /* noop */ } }
        // assistindo: pede a transmissão de novo. Se a partida já acabou, o servidor
        // responde 'partida_encerrada' e a tela volta para a arena com o aviso.
        else if (f2 === 'assistindo' && assistindoId.current) {
          try { ws.current?.send(JSON.stringify({ t: 'watch', match_id: assistindoId.current })); } catch (e) { /* noop */ }
        }
      });
      // dentro da batalha a reconexão é obrigatória: o servidor devolve o estado oficial
      if (f === 'partida' || f === 'assistindo' || f === 'match') { setTimeout(voltar, 300); return; }
      if (f === 'sala' || f === 'fila') { setRestante(0); setFase('sala'); setTimeout(voltar, 1200); return; }
      setFase('idle');
      // na arena a conexão também volta sozinha (deploy do servidor, troca de rede, tela
      // bloqueada). Sem isso a lista ao vivo ficava vazia até o jogador tocar em algo.
      const n = (tentativas.current = Math.min(6, (tentativas.current || 0) + 1));
      setTimeout(() => {
        if (ws.current || document.visibilityState !== 'visible') return;
        if (faseRef.current === 'idle') voltar();
      }, Math.min(7000, 900 * n));
    };
    // só avisa se a conexão realmente falhou (nunca abriu e não foi fechada por nós)
    s.onerror = () => { if (ws.current === s && !s.manual && !s.aberto) aviso('Não foi possível conectar na arena ao vivo.'); };
    s.onmessage = (ev) => {
      if (ws.current !== s) return;          // nada de conexão antiga mexendo na tela
      s.ultimo = Date.now();
      let m = {};
      try { m = JSON.parse(ev.data); } catch (e) { return; }
      if (m.t === 'lobby') {
        setLobby({ online: m.online, queue: m.queue, matches: m.matches || [] });
        setRoom((r) => ({ ...r, members: m.room || r.members, search_seconds: m.search_seconds || r.search_seconds }));
        return;
      }
      if (m.t === 'room') { setRoom(m); return; }
      if (m.t === 'room_left') { setFase('idle'); setRestante(0); return; }
      if (m.t === 'queued') { setRestante(Number(m.seconds || 120)); setFase('fila'); return; }
      if (m.t === 'no_opponent') {
        setRestante(0); setFase('sala');
        gbSfx('deny');
        gbAviso.erro('Ninguém combinou o duelo — nada foi cobrado de você.');
        return;
      }
      // convite direto: alguém da sala me chamou para duelar
      if (m.t === 'invited') {
        setConvite({ id: m.invite_id, de: m.frm || {}, seconds: Number(m.seconds || 30) });
        gbSfx('match');
        return;
      }
      if (m.t === 'invite_sent') {
        const alvo = m.to || {};
        setEnviados((v) => [...v.filter((x) => x.kako_id !== alvo.kako_id),
          { kako_id: alvo.kako_id, invite_id: m.invite_id, nickname: alvo.nickname }]);
        gbAviso.ok(`Convite enviado para ${alvo.nickname || 'o jogador'} — ${Number(m.seconds || 30)}s para aceitar.`);
        return;
      }
      if (m.t === 'invite_end') {
        setEnviados((v) => v.filter((x) => x.invite_id !== m.invite_id));
        setConvite((c) => (c && c.id === m.invite_id ? null : c));
        const quem = m.nickname || 'O jogador';
        const texto = {
          recusado: `${quem} recusou o duelo.`,
          expirou: 'O convite expirou.',
          cancelado: `${quem} cancelou o convite.`,
          oponente_saiu: `${quem} saiu da sala.`,
          cancelado_por_voce: 'Convite cancelado.',
          recusado_por_voce: 'Convite recusado.',
        }[m.reason];
        if (texto) gbAviso.info(texto);
        return;
      }
      if (m.t === 'invite_nok') {
        gbAviso.erro({
          fora_da_sala: 'Esse jogador não está mais na sala.',
          em_duelo: 'Esse jogador já está em um duelo.',
          ja_convidado: 'Você já convidou esse jogador — aguarde a resposta.',
          oponente_saiu: 'Quem te convidou saiu da sala.',
          expirou: 'Esse convite já expirou.',
        }[m.reason] || 'Não foi possível convidar agora.');
        return;
      }
      if (m.t === 'afk_on') { setAfk({ nick: m.opponent, seconds: m.seconds }); paraJogo.current?.('gb:pvp', m); return; }
      if (m.t === 'afk_off') { setAfk(null); paraJogo.current?.('gb:pvp', m); return; }
      if (m.t === 'left') {
        // saiu da transmissão: volta para a ARENA (capa), não para a sala de combinação
        espectando.current = false; assistindoId.current = ''; fimAssistido.current = false;
        setMatch(null); setPlateia(0); setFase('idle');
        try { s.send(JSON.stringify({ t: 'lobby' })); } catch (e) { /* noop */ }
        return;
      }
      if (m.t === 'match') {
        espectando.current = !!m.spectator;
        if (m.spectator) { assistindoId.current = m.match_id; fimAssistido.current = false; }
        setConvite(null); setEnviados([]);
        setPlateia(Number(m.spectators || 0)); setRestante(0); setAfk(null);
        setMatch(m);
        if (m.spectator || m.resumed) { setFase(m.spectator ? 'assistindo' : 'partida'); return; }
        gbSfx('match');
        setFx({
          eu: { nickname: m.me?.nickname, avatar: avatarUrl,
            clan_name: clan?.name || '', medal_img: clan?.medal_img || '' },
          rival: { nickname: m.opponent?.nickname, avatar: m.opponent?.avatar || '',
            clan_name: m.opponent?.clan_name || '', medal_img: m.opponent?.medal_img || '' },
        });
        setFase('match');
        setTimeout(() => { setFx(null); setFase('partida'); }, 1500);
        return;
      }
      else if (m.t === 'plateia') {
        setPlateia(Number(m.count || 0));
        paraJogo.current?.('gb:pvp', { t: 'plateia', count: Number(m.count || 0) });
        return;
      }
      else if (m.t === 'over') {
        if (m.spectator) fimAssistido.current = true;
        if (!m.spectator) onFinished?.();
        paraJogo.current?.('gb:pvp', m);
      }
      else if (m.t === 'error') {
        // o jogo abriu em outro aparelho/aba: essa tela só sai de cena, sem alarme
        if (m.message === 'sessao_substituida') { s.substituida = true; return; }
        // a partida que ele assistia terminou (ou ele clicou num duelo que já acabou):
        // avisa e devolve para a arena, sem deixar a tela do jogo congelada
        if (m.message === 'partida_encerrada') {
          if (faseRef.current === 'assistindo' || espectando.current) {
            sairDaTransmissao('Essa partida já terminou — você voltou para a arena.');
          } else {
            gbAviso.info('Essa partida já terminou.');
            pedirLobby();
          }
          return;
        }
        // já está em partida: o servidor manda a batalha logo atrás, não mexe na tela
        if (m.message === 'em_partida') { aviso('Você já está em uma partida — voltando para ela.'); return; }
        // manutenção programada: o admin travou combinações novas antes de publicar
        if (m.message === 'manutencao_pvp') {
          aviso(m.detail || 'Manutenção programada: as combinações e as fases voltam em breve.');
          const fm = faseRef.current;
          if (fm === 'partida' || fm === 'assistindo' || fm === 'match') return;
          setFase('sala');
          return;
        }
        // evento desativado ou encerrado: não entra duelo novo (quem está jogando termina)
        aviso(m.message === 'evento_fechado' ? 'O evento foi encerrado — não há mais duelos novos.'
          : m.message === 'arena_fechada' ? 'A Arena está fechada agora.'
            : m.message === 'partida_encerrada' ? 'Essa partida já acabou.' : 'Sessão inválida, entre novamente.');
        const f = faseRef.current;
        if (f === 'partida' || f === 'assistindo' || f === 'match') return;   // nunca derruba a batalha
        setFase('idle');
      }
      else if (m.t === 'match_cancel') {
        aviso(m.reason === 'saldo_insuficiente'
          ? `Você precisa de ${Number(m.needed || 0).toLocaleString('pt-BR')} moedas amarelas para entrar no duelo.`
          : m.reason === 'oponente_sem_saldo' ? 'O adversário não tinha saldo — sua entrada foi devolvida.'
            : 'A Arena está fechada agora.');
        setFase('idle'); onFinished?.(); return;
      }
      else if (m.t === 'pong') {
        // reserva: só vale se o medidor em thread separada não estiver respondendo
        const saiu = Number(m.ts);
        if (!Number.isFinite(saiu)) return;
        if (Date.now() - medidoEm.current < 5000) return;
        const ms = Math.max(1, Math.round(performance.now() - saiu));
        pingEnv.current = ms;
        paraJogo.current?.('gb:ping', { ms });
        return;
      }
      else if (m.t === 'coins') { onFinished?.(); return; }
      // item consumido na partida: recarrega a carteira para o estoque bater na lojinha
      else if (m.t === 'item_ok') { onFinished?.(); paraJogo.current?.('gb:pvp', m); return; }
      else if (m.t === 'coinflip') { flip.current = m; paraJogo.current?.('gb:pvp', m); return; }
      else if (m.t === 'cancelled') { setRestante(0); setFase((f) => (f === 'fila' ? 'sala' : f)); }
      else if (m.t !== 'hello' && m.t !== 'queued') paraJogo.current?.('gb:pvp', m);
    };
  }, [aviso, fechar, pedirLobby, sairDaTransmissao]);

  useEffect(() => { faseRef.current = fase; }, [fase]);

  // a página usa a fase para saber qual trilha toca (sala, capa ou nenhuma dentro da partida)
  useEffect(() => { onFase?.(fase); }, [fase, onFase]);

  useEffect(() => { conectarRef.current = conectar; }, [conectar]);

  // medidor de rede em thread separada: o delay mostrado é o da CONEXÃO, não o do desenho
  useEffect(() => {
    let w = null;
    try { w = new Worker('/gb/ping.js'); } catch (e) { return undefined; }
    medidor.current = w;
    w.onmessage = (ev) => {
      const ms = Number((ev.data || {}).ms);
      if (!Number.isFinite(ms)) return;
      medidoEm.current = Date.now();
      pingEnv.current = ms;
      paraJogo.current?.('gb:ping', { ms });
    };
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const proto = base.startsWith('https') ? 'wss' : 'ws';
    const alvo = `${base.replace(/^https?/, proto)}/api/gb/pvp/ping`
      + `?token=${encodeURIComponent(localStorage.getItem('customer_token') || '')}`;
    w.postMessage({ t: 'start', url: alvo });
    return () => {
      try { w.postMessage({ t: 'stop' }); w.terminate(); } catch (e) { /* noop */ }
      medidor.current = null;
    };
  }, []);

  useEffect(() => {
    if (fase !== 'fila') return;
    const id = setInterval(() => setRestante((v) => (v > 0 ? v - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [fase]);

  // o jogo só existe depois do loader: reenvia o sorteio quando a ponte abre
  useEffect(() => {
    if (fase !== 'partida' || !flip.current) return;
    const id = setInterval(() => {
      if (paraJogo.current && flip.current) { paraJogo.current('gb:pvp', flip.current); flip.current = null; }
    }, 300);
    const t = setTimeout(() => { clearInterval(id); flip.current = null; }, 12000);
    return () => { clearInterval(id); clearTimeout(t); };
  }, [fase]);

  useEffect(() => {
    conectar();
    // durante a partida a página para de pedir o lobby: nada de re-render em cima do jogo
    const id = setInterval(() => {
      const f = faseRef.current;
      if (f === 'partida' || f === 'assistindo' || f === 'match') return;
      if (ws.current?.readyState === 1) ws.current.send(JSON.stringify({ t: 'lobby' }));
    }, 4000);
    return () => { clearInterval(id); fechar(); };
  }, [conectar, fechar]);

  // celular que troca de aplicativo: ao voltar, confere o que está vivo de verdade.
  // Assistindo, pede a transmissão de novo — se o duelo acabou, volta para a arena.
  useEffect(() => {
    const aoVoltar = () => {
      if (document.visibilityState !== 'visible') return;
      const f = faseRef.current;
      if (f === 'assistindo') {
        if (fimAssistido.current || !assistindoId.current) {
          sairDaTransmissao('Essa partida já terminou — você voltou para a arena.');
          return;
        }
        conectarRef.current?.(() => {
          try { ws.current?.send(JSON.stringify({ t: 'watch', match_id: assistindoId.current })); } catch (e) { /* noop */ }
        });
        return;
      }
      if (f === 'idle' || f === 'sala' || f === 'fila') pedirLobby();
    };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => document.removeEventListener('visibilitychange', aoVoltar);
  }, [pedirLobby, sairDaTransmissao]);

  const entrarSala = () => {
    gbSfx('click');
    setFase('sala');
    conectar(() => ws.current?.send(JSON.stringify({ t: 'room_join' })));
  };

  const sairSala = () => {
    gbSfx('click');
    try { ws.current?.send(JSON.stringify({ t: 'room_leave' })); } catch (e) { /* noop */ }
    setConvite(null); setEnviados([]);
    setRestante(0); setFase('idle');
  };

  // convite direto: escolhe o oponente que está na sala em vez de esperar a fila
  const convidar = (id) => {
    gbSfx('click');
    conectar(() => { try { ws.current?.send(JSON.stringify({ t: 'invite', to: id })); } catch (e) { /* noop */ } });
  };

  const cancelarConvite = (id) => {
    const c = enviados.find((x) => x.kako_id === id);
    if (!c) return;
    gbSfx('click');
    try { ws.current?.send(JSON.stringify({ t: 'invite_cancel', invite_id: c.invite_id })); } catch (e) { /* noop */ }
    setEnviados((v) => v.filter((x) => x.invite_id !== c.invite_id));
  };

  const aceitarConvite = () => {
    if (!convite) return;
    gbSfx('coin');
    const id = convite.id;
    setConvite(null);
    setFase('sala');
    conectar(() => { try { ws.current?.send(JSON.stringify({ t: 'invite_accept', invite_id: id })); } catch (e) { /* noop */ } });
  };

  const recusarConvite = () => {
    if (!convite) return;
    gbSfx('click');
    try { ws.current?.send(JSON.stringify({ t: 'invite_decline', invite_id: convite.id })); } catch (e) { /* noop */ }
    setConvite(null);
  };

  // lista de partidas ao vivo na mão do jogador: às vezes o pulso automático atrasa
  const atualizarLive = () => {
    gbSfx('click');
    setAtualizando(true);
    pedirLobby();
    setTimeout(() => setAtualizando(false), 800);
  };

  const buscar = () => {
    gbSfx('coin');
    setRestante(room.search_seconds || 120);
    setFase('fila');
    conectar(() => ws.current?.send(JSON.stringify({ t: 'find' })));
  };

  const assistir = (id) => {
    conectar(() => ws.current?.send(JSON.stringify({ t: 'watch', match_id: id })));
  };

  const cancelar = () => {
    gbSfx('click');
    try { ws.current?.send(JSON.stringify({ t: 'cancel' })); } catch (e) { /* noop */ }
    setRestante(0); setFase('sala');
  };

  // enviar do jogo para o servidor: função estável, sem recriar a ponte a cada render
  const enviarPvp = useCallback((p) => {
    try { ws.current?.send(JSON.stringify(p)); } catch (e) { /* noop */ }
  }, []);

  const fecharBatalha = useCallback(() => {
    if (espectando.current) {
      try { ws.current?.send(JSON.stringify({ t: 'unwatch' })); } catch (e) { /* noop */ }
      espectando.current = false;
      assistindoId.current = ''; fimAssistido.current = false;
      setMatch(null); setPlateia(0); onFinished?.();
      setFase('idle');                        // sai da transmissão direto para a arena
      pedirLobby();
      return;
    }
    // quem duelou volta para a sala de combinação: só sai dela clicando em SAIR
    setMatch(null); onFinished?.();
    setFase('sala');
    try { ws.current?.send(JSON.stringify({ t: 'room_join' })); } catch (e) { /* noop */ }
  }, [onFinished]);

  // config da batalha memorizada: o iframe do jogo nunca é remontado por re-render da página
  const cfgBatalha = useMemo(() => (match ? {
    mode: fase === 'assistindo' ? 'spec' : 'pvp',
    match_id: match.match_id,
    terrain_seed: match.seed,
    match_seconds: match.match_seconds || 300,
    turn_seconds: match.turn_seconds || 25,
    features: [],
    map: 'castle',
    // lado B vê o mundo espelhado: o avião do reforço aéreo cruza no sentido contrário
    // para a caixa cair no mesmo instante nos dois aparelhos (e na transmissão)
    espelhado: fase !== 'assistindo' && Number(match.side) === 2,
    bot_hp: match.max_hp,
    wind_max: 14,
    node: 0,
    node_name: 'PVP AO VIVO',
    upgrades: player?.upgrades || {},
    items: player?.items || {},
    opponent: match.opponent,
    spectators: plateia,
    state: match.state || null,      // mundo em andamento: buracos, posições, vida, relógio
    player: {
      nickname: match.me?.nickname || player?.nickname || 'Você',
      // a cor/escudo vem do SERVIDOR (match.me): assistindo, o lado A é quem aparece aqui,
      // então usar o clã de quem assiste pintaria o tanque errado
      avatar: match.me?.avatar || avatarUrl || '',
      color: match.me?.color || clan?.hex || '',
      clan: match.me?.clan || clan?.name || '',
      clan_name: match.me?.clan_name || clan?.name || '',
      medal_img: match.me?.medal_img || clan?.medal_img || '',
    },
  } : null), [match, fase, player, plateia, avatarUrl, clan]);

  if (fase === 'match' && fx) return <GbMatchFx eu={fx.eu} rival={fx.rival} />;

  if ((fase === 'partida' || fase === 'assistindo') && cfgBatalha) {
    return (
      <GbBattle
        pvp={cfgBatalha}
        pvpRef={paraJogo}
        onPvpSend={enviarPvp}
        onClose={fecharBatalha}
      />
    );
  }

  const entrada = Number(config?.pvp_entry_coins || 0);
  const premio = Number(config?.pvp_winner_coins || 0);
  const premiacao = Math.max(0, entrada * 2 - premio);   // o que cada duelo deixa para a premiação
  const naSala = (room.members || []).length;
  const semSaldo = Number(player?.coins || 0) < entrada;

  const REGRAS = [
    { Icon: Coins, t: `Entrada ${entrada.toLocaleString('pt-BR')} moedas`, d: 'cobrada só quando o duelo começa' },
    { Icon: Trophy, t: `Vitória ${premio.toLocaleString('pt-BR')} moedas`, d: 'mais até 100 XP · quem perde não leva moedas' },
    { Icon: Gift, t: `+${premiacao.toLocaleString('pt-BR')} para a premiação`, d: 'de cada duelo, distribuído nos prêmios do evento' },
    { Icon: Timer, t: '5 min · turnos de 25s', d: 'terreno simétrico, sem vantagem para ninguém' },
  ];

  const DETALHES = [
    'O terreno é sorteado igual para os dois lados — ninguém começa em vantagem.',
    'Quem começa é sorteado na hora do match e aparece na tela para os dois.',
    'Cada turno dura 25 segundos; sem tiro, passa a vez.',
    'A partida toda dura no máximo 5 minutos. No fim do tempo, ganha quem estiver com mais vida.',
    'Cair no abismo elimina na hora — cuidado ao andar na beirada.',
    'Desistir no meio dá a vitória ao adversário.',
    'Se cair a internet você tem 90 segundos para voltar, depois perde por abandono.',
    'Cada conta joga de um aparelho só: entrar em outro desconecta o primeiro.',
    'No inventário você pode levar 1 de cada item por partida.',
    'A entrada é devolvida se ninguém combinar o duelo em 2 minutos.',
  ];

  return (
    <div className="pb-10" data-testid="gb-pvp">
      <div className="gb-rise relative overflow-hidden rounded-3xl border border-amber-500/35 shadow-[0_18px_50px_rgba(0,0,0,.75)] [animation-delay:140ms]"
        style={{ backgroundImage: 'url(/gb/arena_pvp.jpg)', backgroundSize: 'cover', backgroundPosition: '50% 52%' }}>
        <span className="pointer-events-none absolute -left-16 -top-20 h-56 w-56 rounded-full bg-orange-600/25 blur-3xl" />
        <span className="pointer-events-none absolute -bottom-20 -right-16 h-56 w-56 rounded-full bg-cyan-500/20 blur-3xl" />
        <div className="relative bg-[linear-gradient(176deg,rgba(8,4,13,.92)_0%,rgba(8,4,13,.76)_38%,rgba(8,4,13,.5)_60%,rgba(8,4,13,.26)_82%,rgba(8,4,13,.46)_100%)] px-4 py-6 sm:px-7 sm:py-8">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.28em] text-orange-400/80">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                </span>
                arena ao vivo
              </p>
              <h2 className="mt-1.5 text-3xl font-black leading-none tracking-tight text-amber-100 sm:text-4xl">
                DUELO PVP <span className="text-orange-400">1v1</span>
              </h2>
            </div>
            <div className="flex flex-none flex-col items-center gap-1">
              <button onClick={() => setRegras(true)} data-testid="gb-pvp-rules-btn" title="Regras do PvP"
                className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-amber-500/70 bg-black/50 text-lg font-black text-amber-300 transition-transform hover:scale-110 hover:border-amber-400">
                ?
              </button>
              <button onClick={() => setRegras(true)} data-testid="gb-pvp-rules-link"
                className="text-[10px] font-black uppercase tracking-wider text-amber-300/80 underline decoration-dotted underline-offset-2 hover:text-amber-200">
                ver regras
              </button>
            </div>
          </div>

          <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-bold text-purple-200/75"
            data-testid="gb-pvp-resumo">
            <span className="flex items-center gap-1"><Coins className="h-3.5 w-3.5 text-amber-400" />entrada {entrada.toLocaleString('pt-BR')}</span>
            <span className="text-purple-700">·</span>
            <span className="flex items-center gap-1"><Trophy className="h-3.5 w-3.5 text-amber-400" />vitória {premio.toLocaleString('pt-BR')}</span>
            <span className="text-purple-700">·</span>
            <span className="flex items-center gap-1"><Timer className="h-3.5 w-3.5 text-amber-400" />5 min</span>
          </p>

          {fase === 'idle' && travado && (
            <div className="mt-5 rounded-2xl border-2 border-amber-500/70 bg-amber-950/45 px-4 py-3"
              data-testid="gb-pvp-maintenance-home">
              <p className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-amber-400">
                <Wrench className="h-3.5 w-3.5" />manutenção programada
              </p>
              <p className="mt-1 text-sm font-bold leading-snug text-amber-100">
                {avisoTrava || 'As combinações estão pausadas e voltam em breve.'}
              </p>
              <p className="mt-1 text-[11px] text-amber-200/75">
                As fases da campanha também estão pausadas. As partidas que já começaram
                seguem até o fim, com prêmio normal.
              </p>
            </div>
          )}

          {fase === 'idle' && (
            <button onClick={entrarSala} data-testid="gb-pvp-enter-room"
              className="gb-brilho group relative mt-5 flex w-full items-center gap-3 overflow-hidden rounded-2xl bg-gradient-to-r from-orange-600 via-orange-500 to-amber-400 px-5 py-4 text-left shadow-[0_0_34px_rgba(255,107,0,.4)] transition-transform hover:scale-[1.015] active:scale-[.985]">
              <Radar className="h-6 w-6 flex-none text-[#1b0d02]" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-black uppercase tracking-wide text-[#1b0d02]">
                  entrar na sala de combinação
                </span>
                <span className="block text-[11px] font-bold text-[#1b0d02]/70">
                  {travado ? 'combinações pausadas — entre e aguarde a liberação'
                    : naSala > 0 ? `${naSala} ${naSala === 1 ? 'jogador esperando' : 'jogadores esperando'} agora`
                      : 'sala aberta — seja o primeiro'}
                </span>
              </span>
              <span className="flex flex-none items-center gap-1.5 rounded-full bg-[#1b0d02]/85 px-3 py-1.5">
                <span className="flex -space-x-2">
                  {(room.members || []).slice(0, 3).map((m) => (
                    m.avatar
                      ? <img key={m.kako_id} src={m.avatar} alt="" className="h-6 w-6 rounded-full border border-amber-400/80 object-cover" />
                      : <span key={m.kako_id} className="flex h-6 w-6 items-center justify-center rounded-full border border-amber-400/80 bg-purple-900 text-[9px]">🎃</span>
                  ))}
                </span>
                <span className="font-mono text-sm font-black text-amber-300">{naSala}</span>
              </span>
            </button>
          )}

        </div>
      </div>

      {regras && (
        <div className="pwa-safe-top fixed inset-0 z-[70] flex items-end justify-center bg-black/85 p-3 sm:items-center"
          data-testid="gb-pvp-rules-modal" onClick={() => setRegras(false)}>
          <div className="max-h-[82vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-amber-700/60 bg-[#120a1d] p-5"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-lg font-black text-amber-200">REGRAS DO DUELO PVP</h3>
              <button onClick={() => setRegras(false)} data-testid="gb-pvp-rules-close"
                className="rounded-full border border-purple-700/60 p-2 text-purple-200">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-3 grid gap-2">
              {REGRAS.map(({ Icon, t, d }) => (
                <div key={t} className="flex items-start gap-2.5 rounded-2xl border border-amber-800/40 bg-amber-950/15 px-3 py-2.5">
                  <Icon className="mt-[1px] h-4 w-4 flex-none text-amber-400" />
                  <div>
                    <p className="text-xs font-black uppercase tracking-wide text-amber-200">{t}</p>
                    <p className="text-[11px] text-purple-200/70">{d}</p>
                  </div>
                </div>
              ))}
            </div>
            <ul className="mt-4 space-y-2">
              {DETALHES.map((r) => (
                <li key={r} className="flex gap-2 text-[12px] leading-snug text-purple-100/85">
                  <Swords className="mt-[2px] h-3.5 w-3.5 flex-none text-orange-400" />{r}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {(fase === 'sala' || fase === 'fila') && (
        <GbRoom room={room} meuId={player?.kako_id} buscando={fase === 'fila'} restante={restante}
          entrada={entrada} saldoOk={!semSaldo} travado={!!travado} avisoTrava={avisoTrava}
          convite={convite} enviados={enviados}
          onInvite={convidar} onCancelInvite={cancelarConvite}
          onAcceptInvite={aceitarConvite} onDeclineInvite={recusarConvite}
          onFind={buscar} onCancel={cancelar} onLeave={sairSala} />
      )}

      {afk && (
        <div className="mt-3 flex items-center gap-3 rounded-2xl border border-amber-700/60 bg-amber-950/25 px-4 py-3"
          data-testid="gb-pvp-afk">
          <Loader2 className="h-4 w-4 animate-spin text-amber-300" />
          <p className="text-sm font-bold text-amber-100">
            <b>{afk.nick}</b> caiu da conexão — o servidor está jogando por ele até voltar.
          </p>
        </div>
      )}

      <div className="mt-5" data-testid="gb-pvp-live">
        <h3 className="mb-2 flex items-center gap-2 text-sm font-black uppercase tracking-wider text-orange-300">
          <Radio className="h-4 w-4 animate-pulse text-rose-400" />partidas ao vivo
          <span className="rounded-full bg-rose-600/20 px-2 py-[1px] text-[10px] font-black text-rose-200">
            {lobby.matches.length}
          </span>
          <button onClick={atualizarLive} data-testid="gb-live-refresh" title="Atualizar lista de partidas"
            className="flex items-center gap-1.5 rounded-full border border-purple-700/60 bg-black/40 px-3 py-1 text-[10px] font-black uppercase tracking-wider text-purple-200 transition-colors hover:border-orange-500/70 hover:text-orange-300 active:scale-95">
            <RefreshCw className={`h-3.5 w-3.5 ${atualizando ? 'animate-spin' : ''}`} />
            atualizar
          </button>
          {!conectado && (
            <span className="flex items-center gap-1.5 rounded-full border border-amber-600/50 bg-amber-950/50 px-2.5 py-1 text-[10px] font-bold text-amber-300"
              data-testid="gb-live-reconectando">
              <Loader2 className="h-3 w-3 animate-spin" />conectando…
            </span>
          )}
        </h3>
        {lobby.matches.length === 0 && (
          <p className="rounded-2xl border border-dashed border-purple-800/60 bg-black/25 px-4 py-5 text-center text-xs text-purple-300/60">
            Nenhum duelo rolando agora. Entre na sala e seja o primeiro da noite.
          </p>
        )}
        <div className="custom-scrollbar max-h-[46vh] overflow-y-auto overscroll-contain pr-1 xl:max-h-[440px]"
          data-testid="gb-live-scroll">
          <div className="grid gap-3 lg:grid-cols-2">
            {lobby.matches.map((m) => (
              <div key={m.match_id} data-testid={`gb-live-${m.match_id}`}
                className="relative overflow-hidden rounded-2xl border border-purple-800/60 bg-gradient-to-b from-[#180d2c] via-[#120822] to-[#0d0517] p-3.5 transition-colors hover:border-purple-600/70">
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1.5 rounded-full border border-purple-800/60 bg-black/60 px-2.5 py-0.5 font-mono text-[10px] font-black text-amber-200">
                    <Timer className="h-3 w-3 text-amber-400" />{mmss(m.seconds)}
                  </span>
                  <span className="rounded-full border border-purple-800/60 bg-black/50 px-2.5 py-0.5 text-[10px] font-bold text-purple-200/80">
                    turno {m.index}
                  </span>
                  <span className="ml-auto flex items-center gap-1.5 rounded-full border border-rose-800/50 bg-rose-950/40 px-2.5 py-0.5 text-[10px] font-bold text-rose-200"
                    title="pessoas assistindo agora" data-testid={`gb-live-spectators-${m.match_id}`}>
                    <Eye className="h-3 w-3 animate-pulse text-rose-400" />{m.spectators}
                  </span>
                </div>

                <div className="mt-3 flex items-stretch gap-2">
                  <Lutador p={m.a} max={m.max_hp} />
                  <div className="flex flex-none items-center">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full border border-orange-500/60 bg-[#250f14] text-[10px] font-black text-orange-400 shadow-[0_0_12px_rgba(249,115,22,.45)]">
                      VS
                    </span>
                  </div>
                  <Lutador p={m.b} max={m.max_hp} dir />
                </div>

                <button onClick={() => assistir(m.match_id)} data-testid={`gb-watch-${m.match_id}`}
                  className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-rose-600 to-rose-500 py-2 text-[11px] font-black uppercase tracking-wider text-white transition-all hover:shadow-[0_0_20px_rgba(244,63,94,.5)]">
                  <Eye className="h-3.5 w-3.5" />assistir
                  {m.spectators > 0 && <span className="rounded-full bg-black/30 px-2 py-[1px] font-mono">{m.spectators}</span>}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
